/**
 * **The pluggable weighted-feature static evaluator** — a pure function `state -> number`,
 * side-relative (positive favors `side`), built as a weighted sum of independent
 * {@link FeatureTerm}s. Deathmatch ships five terms (EV damage output, EV damage intake, graded
 * material, status economy, position/cover); sitrep/objective terms drop in as MORE terms, never
 * a reshape.
 *
 * Every term composes already-exported engine primitives — `evaluateAttackEv` (this package's
 * own EV toolkit, itself composed from `@lancer-vtt/rules`), `terrainVisibility`/`isEngagedWith`
 * for position, `sideOf`/`hasStatus` for material/status. No rule is re-derived here; a term is
 * just an aggregation over already-legal, already-computed engine facts.
 */

import { hasStatus, sideOf, terrainVisibility, worldFor, type Combatant, type EncounterState, type Side } from "@lancer-vtt/domain";
import { isEngagedWith, SEED_OVERLAY, type LoadedOverlay } from "@lancer-vtt/rules";
import { evaluateAttackEv } from "./ev";

export type FeatureContext = {
  state: EncounterState;
  /** Whose perspective to score FOR — a positive total favors this side. */
  side: Side;
  overlay?: LoadedOverlay;
};

export type FeatureTerm = {
  name: string;
  weight: number;
  /** The raw (unweighted) feature value, side-relative: positive favors `ctx.side`. */
  compute: (ctx: FeatureContext) => number;
};

export type TermContribution = { name: string; weight: number; value: number; contribution: number };

export type EvaluationBreakdown = {
  total: number;
  terms: TermContribution[];
};

function livingPlaced(state: EncounterState, side: Side): Combatant[] {
  return Object.values(state.combatants).filter((c) => c.pos != null && sideOf(c) === side && c.current.hp > 0);
}

/** A living, placed unit's best available attack EV damage right now (no movement, no search — a static snapshot of "what could this unit do next"), 0 if it carries no usable weapon or no hostile is reachable. */
function bestAttackEvDamage(state: EncounterState, unit: Combatant, overlay: LoadedOverlay): number {
  const hostiles = Object.values(state.combatants).filter((c) => c.pos != null && sideOf(c) !== sideOf(unit) && c.current.hp > 0);
  let best = 0;
  for (const weapon of unit.base.weapons ?? []) {
    if (weapon.damage.type === "variable") continue; // v1 EV toolkit needs a concrete type; skip rather than guess
    for (const target of hostiles) {
      const ev = evaluateAttackEv({ state, attacker: unit, target, weapon, weaponRef: weapon.instanceId ?? weapon.id, overlay });
      if (ev.expectedDamage > best) best = ev.expectedDamage;
    }
  }
  return best;
}

/** `EV damage output` — the sum of each of `side`'s units' own best-available attack EV (positive: `side` threatens damage). */
export const evDamageOutputTerm: FeatureTerm = {
  name: "ev-damage-output",
  weight: 1,
  compute: ({ state, side, overlay = SEED_OVERLAY }) => livingPlaced(state, side).reduce((sum, u) => sum + bestAttackEvDamage(state, u, overlay), 0),
};

/** `EV damage intake` — the NEGATED sum of the opposing side's own best-available attack EV against `side` (a threat map; more hostile threat is bad for `side`). */
export const evDamageIntakeTerm: FeatureTerm = {
  name: "ev-damage-intake",
  weight: 1,
  compute: ({ state, side, overlay = SEED_OVERLAY }) => {
    const opposing: Side = side === "pc" ? "npc" : "pc";
    return -livingPlaced(state, opposing).reduce((sum, u) => sum + bestAttackEvDamage(state, u, overlay), 0);
  },
};

/**
 * `Graded material` — a monotonic remaining-health fraction (HP+structure folded as ONE track)
 * plus stress-remaining fraction (its own, separate track), summed for `side` minus the
 * opposing side (a smooth "how healthy is my force" signal, not just alive/dead).
 *
 * **HP/structure model (fixed 2026-07-18, `unit-ai` head-to-head diagnosis):** Lancer RAW
 * (Core p.79-80) resolves a structure-cascade hit by decrementing `structure` by 1 AND
 * resetting `hp` to `maxHp` *in the same event* (confirmed against the actual reducer path,
 * `packages/rules/src/encounter/tables.ts`'s `resolveDamageCascade`: the HP-reset-to-max is
 * unconditional on every survived structure loss, not gated on the structure-table roll). The
 * OLD formula (`hp/maxHp + 0.5*structure/maxStructure`, two independent fractions) scored that
 * event as a NET GAIN for the just-hit unit — the HP-reset's jump toward 1.0 outweighs the
 * small structure-fraction drop — even though the unit is objectively closer to destruction.
 * That bug measurably broke the depth-2 search (it faithfully evaluates the real post-attack
 * state via chance nodes, so it learned to avoid landing exactly the hits its own evaluator
 * misjudged as helping the target — see `cli/scripts/unit-ai-headtohead-sweep.ts`'s header).
 *
 * The fix: model HP and structure as ONE monotonic remaining-health measure. A unit at
 * (structure S, hp H) can still absorb `(S-1)*maxHp + H` more damage before destruction — a
 * mech survives depleting its HP bar `maxStructure` times total, so the "boxes" below the
 * current one are each worth a full `maxHp`, and only the CURRENT box's `hp` counts partially.
 * Normalizing by the max possible (`maxStructure*maxHp`, i.e. full structure + full hp) gives a
 * fraction in `[0, 1]` that is **strictly decreasing in cumulative damage taken**: a hit that
 * merely drops `hp` decreases it (fewer hp in the same box); a hit that crosses a structure
 * threshold ALSO decreases it, because the reset lands exactly on the new box's ceiling
 * (`(S-1)*maxHp`), strictly below the pre-hit value (`(S-1)*maxHp + H` for any `H > 0`) — never
 * a gain. A destroyed unit (structure hits 0, or a failed HULL save / Crushing Hit) is not
 * scored by this formula at all: it drops out of `livingPlaced` (hp pinned to 0 by the reducer
 * at the moment of destruction, never reset), which already strictly decreases the side's sum.
 *
 * **Stress is deliberately NOT folded into this — it stays its own independent fraction.** The
 * stress/heat cascade (Core p.80, `resolveHeatCascade`) resets `heat`, not any scored field —
 * heat isn't a `FeatureTerm` input at all — so stress's reset-on-loss has no analogous "scores
 * a loss as a gain" bug: `stress/maxStress` alone is already monotonic in stress lost.
 */
export const materialTerm: FeatureTerm = {
  name: "material",
  weight: 10,
  compute: ({ state, side }) => {
    const score = (s: Side): number =>
      livingPlaced(state, s).reduce((sum, u) => {
        let v: number;
        if (u.base.maxStructure) {
          const structure = u.current.structure ?? u.base.maxStructure;
          // Remaining health before destruction, monotonic in cumulative damage: every box
          // below the current one is a full maxHp, the current box counts its own hp.
          const remaining = (structure - 1) * u.base.maxHp + u.current.hp;
          v = remaining / (u.base.maxStructure * u.base.maxHp);
        } else {
          v = u.current.hp / u.base.maxHp;
        }
        if (u.base.maxStress) v += 0.5 * ((u.current.stress ?? u.base.maxStress) / u.base.maxStress);
        return sum + v;
      }, 0);
    const opposing: Side = side === "pc" ? "npc" : "pc";
    return score(side) - score(opposing);
  },
};

/** Statuses that are unambiguously bad for whoever carries them, regardless of side — a defensible catalog, deliberately not exhaustive. */
const DEBUFF_STATUSES = ["Stunned", "Immobilized", "Shut Down", "Down and Out", "Jammed", "Exposed", "Shredded", "Lock On", "Grappled", "Prone", "Impaired", "Slowed"];

/** `Status economy` — debuffs carried by the opposing side minus debuffs carried by `side` (positive when the enemy is worse off). */
export const statusEconomyTerm: FeatureTerm = {
  name: "status-economy",
  weight: 3,
  compute: ({ state, side }) => {
    const count = (s: Side): number =>
      livingPlaced(state, s).reduce((sum, u) => sum + DEBUFF_STATUSES.filter((name) => hasStatus(u.statuses, name)).length, 0);
    const opposing: Side = side === "pc" ? "npc" : "pc";
    return count(opposing) - count(side);
  },
};

/** `Position` — cover (vs. the nearest living hostile) and pairwise engagement, `side`'s units minus the opposing side's. Cheap geometry (`terrainVisibility`/`isEngagedWith`), not weapon-specific (unlike the EV toolkit's own weapon-gated cover fold in `ev.ts`). */
export const positionTerm: FeatureTerm = {
  name: "position",
  weight: 2,
  compute: ({ state, side }) => {
    const world = worldFor(state);
    const coverAndEngagement = (s: Side): number => {
      const mine = livingPlaced(state, s);
      const theirs = livingPlaced(state, s === "pc" ? "npc" : "pc");
      let score = 0;
      for (const u of mine) {
        if (u.pos == null) continue;
        let bestCover = 0; // 0 none, 1 soft, 2 hard — best (highest) cover against any single hostile
        let engagedCount = 0;
        for (const h of theirs) {
          if (h.pos == null) continue;
          if (isEngagedWith(state, u.id, h.id)) engagedCount++;
          const originFp = world.footprint(h.pos, h.base.size).cells();
          const targetFp = world.footprint(u.pos, u.base.size).cells();
          const view = terrainVisibility({
            space: world,
            terrain: state.terrain,
            from: originFp,
            to: targetFp,
            edges: state.edges,
            combatants: state.combatants,
            excludeCoverFrom: u.id,
            areaEffects: state.areaEffects,
          });
          const deg = view.cover === "hard" ? 2 : view.cover === "soft" ? 1 : 0;
          if (deg > bestCover) bestCover = deg;
        }
        // Cover is good; being engaged by MULTIPLE hostiles at once is bad (ganged up on); a
        // single engagement is neutral (expected melee posture, not penalized on its own).
        score += bestCover - Math.max(0, engagedCount - 1);
      }
      return score;
    };
    return coverAndEngagement(side) - coverAndEngagement(side === "pc" ? "npc" : "pc");
  },
};

/** The default deathmatch weighted-term set. */
export const DEATHMATCH_TERMS: readonly FeatureTerm[] = [evDamageOutputTerm, evDamageIntakeTerm, materialTerm, statusEconomyTerm, positionTerm];

/** Evaluate `ctx.state` for `ctx.side` as a weighted sum of `terms` (defaults to {@link DEATHMATCH_TERMS}) — the evaluator's one entry point. Pure; pinned outputs on fixture states are its validation floor. */
export function evaluateState(ctx: FeatureContext, terms: readonly FeatureTerm[] = DEATHMATCH_TERMS): EvaluationBreakdown {
  const contributions: TermContribution[] = terms.map((t) => {
    const value = t.compute(ctx);
    return { name: t.name, weight: t.weight, value, contribution: t.weight * value };
  });
  const total = contributions.reduce((sum, c) => sum + c.contribution, 0);
  return { total, terms: contributions };
}
